/**
 * Scroll (and focus) the first errored form control into view.
 *
 * After an API validation error is applied to a Formik form, the errored inputs
 * get antd's `*-status-error` marker class (`ant-input-status-error`,
 * `ant-select-status-error`, `ant-picker-status-error`, …). This finds the
 * FIRST such control in DOM order — i.e. the topmost errored field on screen —
 * scrolls it into view, and moves keyboard focus to its input so the user can
 * start correcting immediately.
 *
 * DOM-order (not backend-order) is deliberate: `document.querySelectorAll`
 * returns elements in document order, which matches the visual top-to-bottom
 * layout of the form.
 *
 * The error classes only appear after React re-renders with the new errors, so
 * callers should invoke this on the next frame (see `scrollToFirstFieldError`,
 * which schedules itself).
 */

/**
 * antd applies `<component>-status-error` to any errored control. Controls
 * antd gives no error status (e.g. Switch) mark their cell with
 * `data-field-error` instead.
 */
const ERROR_SELECTOR = '[class*="-status-error"], [data-field-error="true"]'

/** The actual focusable input inside an antd control wrapper, if any. */
const FOCUSABLE_SELECTOR = 'input, textarea, [tabindex]'

const findFirstErroredControl = (root: ParentNode): HTMLElement | null => {
  const matches = Array.from(root.querySelectorAll<HTMLElement>(ERROR_SELECTOR))
  // A single control can carry the marker on nested wrappers (e.g. antd Select
  // sets it on both the outer and inner element). Filter to the outermost of any
  // nested pair so we scroll to the control, not a descendant of one already in
  // the list.
  return (
    matches.find(
      (el) => !matches.some((other) => other !== el && other.contains(el))
    ) ??
    matches[0] ??
    null
  )
}

/**
 * Scroll+focus the first errored control under `container` (defaults to the
 * whole document). Runs on the next animation frame so the error classes have
 * been painted. No-op when nothing is errored.
 */
export const scrollToFirstFieldError = (
  container?: HTMLElement | null
): void => {
  // Guard for non-DOM environments (tests without jsdom, SSR).
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return
  }

  const run = () => {
    const root: ParentNode = container ?? document
    const control = findFirstErroredControl(root)
    if (!control) {
      return
    }
    control.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const focusable =
      control.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ?? control
    // preventScroll: we've already centered the control; let focus not fight it.
    focusable.focus?.({ preventScroll: true })
  }

  if (typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(run)
  } else {
    run()
  }
}
